import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { ApolloClient, InMemoryCache, ApolloProvider, createHttpLink, useMutation, gql } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { useAuth } from '../contexts/AuthContext';
import ReportView from '../components/reports/ReportView';
import WorkOrderReportView from '../components/reports/WorkOrderReportView';
import { ReportType } from '../components/reports/ReportTypeSelector';
import { RoleFilter } from '../components/reports/RoleFilterSelector';
import { exportReportToExcel } from '../utils/excelExport';
import { exportWorkOrdersToExcel } from '../utils/workOrderExcelExport';
import { generatePayrollReportName } from '../utils/reportNaming';
import { toast, Toaster } from 'react-hot-toast';

// GraphQL mutation for generating a share token
const GENERATE_REPORT_SHARE_TOKEN = gql`
  mutation GenerateReportShareToken($input: GenerateReportShareTokenInput!) {
    generateReportShareToken(input: $input) {
      token
      expiresAt
    }
  }
`;

// Create a separate Apollo client for public requests
const httpLink = createHttpLink({
  uri: process.env.REACT_APP_GRAPHQL_URL || 'http://localhost:3001/api/graphql',
  credentials: 'include',
  fetchOptions: {
    mode: 'cors'
  }
});

// Create the auth link (without requiring authentication)
const authLink = setContext((_, { headers }) => {
  return {
    headers: {
      ...headers,
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    }
  }
});

// Create a more robust Apollo Client for public requests with better error handling
const publicClient = new ApolloClient({
  link: authLink.concat(httpLink),
  cache: new InMemoryCache(),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'network-only',
      errorPolicy: 'all',
    },
    query: {
      fetchPolicy: 'network-only',
      errorPolicy: 'all',
    },
    mutate: {
      errorPolicy: 'all',
    }
  },
  connectToDevTools: true,
});

// Component that handles legacy share links by generating a token
function LegacyShareHandler({ reportId, onTokenGenerated, onError, navigate }) {
  const [generateToken] = useMutation(GENERATE_REPORT_SHARE_TOKEN);
  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    const handleLegacyShare = async () => {
      setIsGenerating(true);
      try {
        const { data } = await generateToken({
          variables: {
            input: { reportId }
          }
        });
        
        if (data?.generateReportShareToken?.token) {
          onTokenGenerated(data.generateReportShareToken.token);
        } else {
          console.error('No token returned from generateReportShareToken');
          onError('Failed to generate access token');
        }
      } catch (error) {
        console.error('Error generating token for legacy share:', error);
        onError('Failed to generate access token. This link may have expired.');
      } finally {
        setIsGenerating(false);
      }
    };

    handleLegacyShare();
  }, [reportId, generateToken, onTokenGenerated, onError]);

  if (isGenerating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Generating access token...</p>
        </div>
      </div>
    );
  }

  return null;
}

export function SharedReportDetails() {
  const { reportId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isValidToken, setIsValidToken] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [legacyShareMode, setLegacyShareMode] = useState(false);
  const [generatedToken, setGeneratedToken] = useState(null);
  const [reportDetails, setReportDetails] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Extract token from the URL or use generated token
  const token = searchParams.get('token') || generatedToken;
  const isLegacyShare = searchParams.get('share') === 'true';
  
  useEffect(() => {
    // For public access, check if we have a token or need to generate one
    if (!token && !isLegacyShare) {
      setError('Invalid or missing access token. This link may have expired.');
      setIsLoading(false);
      return;
    }
    
    // If we have a legacy share flag but no token, we need to generate one
    if (isLegacyShare && !token) {
      setLegacyShareMode(true);
      setIsLoading(false);
      return;
    }
    
    // If we have a token, we can proceed
    if (token) {
      setIsValidToken(true);
      setIsLoading(false);
      return;
    }
    
    setIsLoading(false);
  }, [token, isLegacyShare, navigate, reportId, user]);

  const handleTokenGenerated = (newToken) => {
    setGeneratedToken(newToken);
    setIsValidToken(true);
    setIsLoading(false);
    setLegacyShareMode(false);
  };

  const handleError = (errorMessage) => {
    console.error('Error in SharedReportDetails:', errorMessage);
    setError(errorMessage);
    setIsLoading(false);
    setLegacyShareMode(false);
  };

  const handleExportExcel = async () => {
    if (!reportDetails) {
      toast.error('Report data not loaded yet. Please wait and try again.');
      return;
    }

    try {
      const { parsedReportData, reportType, roleFilter, startDate, endDate, selectedAreas, reportTitle } = reportDetails;
      
      // Check if this is a Work Orders report
      if (reportType === ReportType.WORK_ORDERS || reportType === 'WORK_ORDERS') {
        // Use work order specific Excel export
        await exportWorkOrdersToExcel(parsedReportData, { 
          startDate, 
          endDate, 
          appliedAreas: parsedReportData?.appliedAreas || selectedAreas || [],
          appliedDepartments: parsedReportData?.appliedDepartments || [],
          appliedStatuses: parsedReportData?.appliedStatuses || []
        });
      } else {
        // Use payroll/housekeeping Excel export
      await exportReportToExcel(parsedReportData, reportType, roleFilter, startDate, endDate, false, selectedAreas, reportTitle);
      }
      
      toast.success('Excel report exported successfully!');
    } catch (error) {
      console.error('Error exporting Excel:', error);
      toast.error('Failed to export Excel. Please try again.');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading report...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-100 rounded-full">
              <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-red-800 mb-2">Access Denied</h3>
            <p className="text-red-600 mb-4">{error}</p>
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              Go to Homepage
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (legacyShareMode) {
    return (
      <ApolloProvider client={publicClient}>
        <LegacyShareHandler
          reportId={reportId}
          onTokenGenerated={handleTokenGenerated}
          onError={handleError}
          navigate={navigate}
        />
      </ApolloProvider>
    );
  }

  if (!isValidToken || !token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Validating access...</p>
        </div>
      </div>
    );
  }

  return (
    <ApolloProvider client={publicClient}>
      <Toaster position="top-right" />
      <div className="min-h-screen bg-gray-50">
        {/* Print Styles */}
        <style jsx>{`
          @media print {
            .no-print {
              display: none !important;
            }
            .print-break {
              page-break-before: always;
            }
            .print-avoid-break {
              page-break-inside: avoid;
            }
            
            /* Force desktop layout for print */
            .print-optimized {
              font-size: 14px !important;
              max-width: none !important;
              margin: 0 !important;
              padding: 20px !important;
            }
            
            .print-optimized table {
              font-size: 12px !important;
              width: 100% !important;
              table-layout: fixed !important;
              border-collapse: collapse !important;
            }
            
            .print-optimized th,
            .print-optimized td {
              padding: 8px 12px !important;
              border: 1px solid #e5e7eb !important;
              vertical-align: top !important;
            }
            
            .print-optimized th {
              background-color: #f9fafb !important;
              font-weight: 600 !important;
            }
            
            /* Force desktop container layout */
            .print-optimized .max-w-7xl {
              max-width: none !important;
              margin: 0 !important;
              padding: 20px !important;
            }
            
            /* Hide mobile-specific elements and force desktop view */
            .print-optimized .sm\\:hidden {
              display: none !important;
            }
            
            .print-optimized .md\\:block {
              display: block !important;
            }
            
            .print-optimized .lg\\:block {
              display: block !important;
            }
            
            /* Force table layout to be visible */
            .print-optimized .hidden {
              display: table !important;
            }
            
            .print-optimized .block {
              display: block !important;
            }
            
            body {
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
              font-size: 14px !important;
            }
            
            .bg-gray-50 {
              background-color: white !important;
            }
            
            .shadow {
              box-shadow: none !important;
            }
            
            .rounded-lg {
              border-radius: 0 !important;
            }
            
            /* Ensure tables don't break awkwardly */
            table {
              page-break-inside: avoid;
              border-collapse: collapse !important;
            }
            
            /* Make sure borders are visible in print */
            .border-gray-200 {
              border-color: #e5e7eb !important;
            }
          }
        `}</style>
        {/* Header with logo branding */}
        <div className="bg-white shadow-sm border-b no-print">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center">
                {/* Logo moved from top right */}
                <div className="flex-shrink-0">
                  <img
                    className="h-8 w-auto"
                    src="/logo-mobile.png"
                    alt="Sweetgrass Properties"
                    onError={(e) => {
                      // Fallback if logo doesn't exist
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'block';
                    }}
                  />
                  <div className="hidden text-xl font-bold text-sky-600">
                    Sweetgrass Properties
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                {/* Desktop Action Buttons */}
                <div className="hidden md:flex items-center space-x-3">
                  <button
                    onClick={handleExportExcel}
                    disabled={!reportDetails}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-emerald-600 border border-transparent rounded-md hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Export XLS
                  </button>
                  <button
                    onClick={() => window.print()}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-gray-600 border border-transparent rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                    </svg>
                    Print Report
                  </button>
                  <button
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(window.location.href);
                        toast.success('Link copied to clipboard!');
                      } catch (err) {
                        console.error('Failed to copy link:', err);
                        toast.error('Failed to copy link');
                      }
                    }}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-purple-600 border border-transparent rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                    </svg>
                    Share Report
                  </button>
                </div>
                
                {/* Mobile Menu Button */}
                <button
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="md:hidden inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-sky-500"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                  Actions
                </button>
              </div>
            </div>
          </div>
          
          {/* Mobile Menu - Stacked Buttons */}
          {mobileMenuOpen && (
            <div className="md:hidden px-4 py-3 border-b border-gray-200 bg-gray-50 space-y-2">
                <button
                  onClick={async () => {
                    await handleExportExcel();
                    setMobileMenuOpen(false);
                  }}
                  disabled={!reportDetails}
                  className="w-full inline-flex items-center justify-center px-4 py-3 text-sm font-medium text-white bg-emerald-600 border border-transparent rounded-md hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Export XLS
                </button>
                <button
                  onClick={() => {
                    window.print();
                    setMobileMenuOpen(false);
                  }}
                  className="w-full inline-flex items-center justify-center px-4 py-3 text-sm font-medium text-white bg-gray-600 border border-transparent rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                  </svg>
                  Print Report
                </button>
                <button
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(window.location.href);
                      toast.success('Link copied to clipboard!');
                      setMobileMenuOpen(false);
                    } catch (err) {
                      console.error('Failed to copy link:', err);
                      toast.error('Failed to copy link');
                    }
                  }}
                  className="w-full inline-flex items-center justify-center px-4 py-3 text-sm font-medium text-white bg-purple-600 border border-transparent rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                  </svg>
                  Share Report
                </button>
            </div>
          )}
        </div>

        {/* Report Content */}
        <div className="max-w-7xl mx-auto px-1 lg:px-8 py-3 lg:py-8">
          <PublicReportView 
            reportId={reportId}
            accessToken={token}
            onReportDataLoaded={setReportDetails}
          />
        </div>
      </div>
    </ApolloProvider>
  );
}

// Component to handle the public report view
function PublicReportView({ reportId, accessToken, onReportDataLoaded }) {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchReport = async () => {
      try {
        setLoading(true);
        const response = await publicClient.query({
          query: gql`
            query GetPublicReport($id: ID!, $token: String) {
              publicReport(id: $id, token: $token) {
                id
                name
                type
                startDate
                endDate
                status
                roleFilter
                reportDataJson
                reportFilePath
                reportFileSize
                totalPayout
                totalCost
                totalExpenses
                finalTotal
                jobsCount
                createdAt
                createdBy {
                  id
                  firstName
                  lastName
                }
                expenses {
                  id
                  description
                  amount
                  createdAt
                }
              }
            }
          `,
          variables: { id: reportId, token: accessToken },
          fetchPolicy: 'network-only'
        });

        if (response.data?.publicReport) {
          setReportData(response.data.publicReport);
        } else {
          setError('Report not found');
        }
      } catch (err) {
        console.error('Error fetching public report:', err);
        setError('Failed to load report');
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [reportId, accessToken]);

  // Pass report details to parent for CSV export when data is loaded
  useEffect(() => {
    if (reportData && onReportDataLoaded) {
      try {
        // Parse the report data JSON
        let parsedReportData = null;
        let selectedAreas = null;
        if (reportData.reportDataJson) {
          parsedReportData = JSON.parse(reportData.reportDataJson);
          // Extract areas from the report data if they exist
          if (parsedReportData && parsedReportData.appliedAreas) {
            selectedAreas = parsedReportData.appliedAreas;
          }
        }

        // Determine report type and role filter
        const reportType = reportData.type || ReportType.HOUSEKEEPING;
        const roleFilter = reportData.roleFilter || RoleFilter.ALL;

        // Validate and format dates safely, handling timestamp strings
        let safeStartDate, safeEndDate;
        
        // Handle startDate
        if (reportData.startDate) {
          if (typeof reportData.startDate === 'string' && /^\d+$/.test(reportData.startDate)) {
            // Timestamp string - extract UTC date
            const tempDate = new Date(parseInt(reportData.startDate, 10));
            const year = tempDate.getUTCFullYear();
            const month = String(tempDate.getUTCMonth() + 1).padStart(2, '0');
            const day = String(tempDate.getUTCDate()).padStart(2, '0');
            safeStartDate = `${year}-${month}-${day}`;
          } else if (typeof reportData.startDate === 'number') {
            // Timestamp number - extract UTC date
            const tempDate = new Date(reportData.startDate);
            const year = tempDate.getUTCFullYear();
            const month = String(tempDate.getUTCMonth() + 1).padStart(2, '0');
            const day = String(tempDate.getUTCDate()).padStart(2, '0');
            safeStartDate = `${year}-${month}-${day}`;
          } else {
            safeStartDate = reportData.startDate;
          }
        } else {
          safeStartDate = new Date().toISOString().split('T')[0];
        }
        
        // Handle endDate
        if (reportData.endDate) {
          if (typeof reportData.endDate === 'string' && /^\d+$/.test(reportData.endDate)) {
            // Timestamp string - extract UTC date
            const tempDate = new Date(parseInt(reportData.endDate, 10));
            const year = tempDate.getUTCFullYear();
            const month = String(tempDate.getUTCMonth() + 1).padStart(2, '0');
            const day = String(tempDate.getUTCDate()).padStart(2, '0');
            safeEndDate = `${year}-${month}-${day}`;
          } else if (typeof reportData.endDate === 'number') {
            // Timestamp number - extract UTC date
            const tempDate = new Date(reportData.endDate);
            const year = tempDate.getUTCFullYear();
            const month = String(tempDate.getUTCMonth() + 1).padStart(2, '0');
            const day = String(tempDate.getUTCDate()).padStart(2, '0');
            safeEndDate = `${year}-${month}-${day}`;
          } else {
            safeEndDate = reportData.endDate;
          }
        } else {
          safeEndDate = new Date().toISOString().split('T')[0];
        }

        const reportTitle = reportData.name || generatePayrollReportName(reportType, roleFilter, selectedAreas || []);

        onReportDataLoaded({
          parsedReportData,
          reportType,
          roleFilter,
          startDate: safeStartDate,
          endDate: safeEndDate,
          selectedAreas,
          reportTitle
        });
      } catch (err) {
        console.error('Error processing report data:', err);
      }
    }
  }, [reportData, onReportDataLoaded]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  if (!reportData) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">No report data available</p>
      </div>
    );
  }

  // Parse the report data JSON for rendering
  let parsedReportData = null;
  let selectedAreas = null;
  try {
    if (reportData.reportDataJson) {
      parsedReportData = JSON.parse(reportData.reportDataJson);
      
      // Extract areas from the report data if they exist
      if (parsedReportData && parsedReportData.appliedAreas) {
        selectedAreas = parsedReportData.appliedAreas;
      }
    }
  } catch (err) {
    console.error('Error parsing report data JSON:', err);
  }

  // Determine report type and role filter
  const reportType = reportData.type || ReportType.HOUSEKEEPING;
  const roleFilter = reportData.roleFilter || RoleFilter.ALL;

  // Validate and format dates safely, handling timestamp strings
  let safeStartDate, safeEndDate;
  
  // Handle startDate
  if (reportData.startDate) {
    if (typeof reportData.startDate === 'string' && /^\d+$/.test(reportData.startDate)) {
      // Timestamp string - extract UTC date
      const tempDate = new Date(parseInt(reportData.startDate, 10));
      const year = tempDate.getUTCFullYear();
      const month = String(tempDate.getUTCMonth() + 1).padStart(2, '0');
      const day = String(tempDate.getUTCDate()).padStart(2, '0');
      safeStartDate = `${year}-${month}-${day}`;
    } else if (typeof reportData.startDate === 'number') {
      // Timestamp number - extract UTC date
      const tempDate = new Date(reportData.startDate);
      const year = tempDate.getUTCFullYear();
      const month = String(tempDate.getUTCMonth() + 1).padStart(2, '0');
      const day = String(tempDate.getUTCDate()).padStart(2, '0');
      safeStartDate = `${year}-${month}-${day}`;
    } else {
      safeStartDate = reportData.startDate;
    }
  } else {
    safeStartDate = new Date().toISOString().split('T')[0];
  }
  
  // Handle endDate
  if (reportData.endDate) {
    if (typeof reportData.endDate === 'string' && /^\d+$/.test(reportData.endDate)) {
      // Timestamp string - extract UTC date
      const tempDate = new Date(parseInt(reportData.endDate, 10));
      const year = tempDate.getUTCFullYear();
      const month = String(tempDate.getUTCMonth() + 1).padStart(2, '0');
      const day = String(tempDate.getUTCDate()).padStart(2, '0');
      safeEndDate = `${year}-${month}-${day}`;
    } else if (typeof reportData.endDate === 'number') {
      // Timestamp number - extract UTC date
      const tempDate = new Date(reportData.endDate);
      const year = tempDate.getUTCFullYear();
      const month = String(tempDate.getUTCMonth() + 1).padStart(2, '0');
      const day = String(tempDate.getUTCDate()).padStart(2, '0');
      safeEndDate = `${year}-${month}-${day}`;
    } else {
      safeEndDate = reportData.endDate;
    }
  } else {
    safeEndDate = new Date().toISOString().split('T')[0];
  }

  // Render Work Order Report View for WORK_ORDERS type
  if (reportType === ReportType.WORK_ORDERS || reportType === 'WORK_ORDERS') {
    return (
      <div className="bg-white rounded-lg shadow">
        <WorkOrderReportView
          reportData={parsedReportData}
          startDate={safeStartDate}
          endDate={safeEndDate}
          savedReportId={reportData.id}
          savedReportName={reportData.name}
          onBackToSelection={() => {}} // No-op for public view
          onReportSaved={() => {}} // No-op for public view
          isPublicView={true}
        />
      </div>
    );
  }

  // Default: Render Housekeeping/Payroll Report View
  return (
    <div className="bg-white rounded-lg shadow">
      <ReportView
        reportData={parsedReportData}
        reportType={reportType}
        roleFilter={roleFilter}
        startDate={safeStartDate}
        endDate={safeEndDate}
        savedReportId={reportData.id}
        savedReportName={reportData.name}
        onBackToSelection={() => {}} // No-op for public view
        selectedAreas={selectedAreas}
        onReportSaved={() => {}} // No-op for public view
        isPublicView={true} // This will be used to show/hide certain elements
      />
    </div>
  );
}
